import React, { useEffect } from 'react'
import SubjectService from 'services/SubjectService'
import { onPageEnter } from 'lib/tracking/pageEnter'
import { Subject, Subject as SubjectInterface } from 'interfaces/Subject'
import styled from 'styled-components'
import Meta from 'components/modules/SEO/Meta'
import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import theme from 'stories/utils/theme'
import { StandardHeadline, StandardSubtitle } from 'stories/elements/Typography/Text'
import { parseLocale } from 'lib/constants/localization'
import Stack from 'components/elements/Stack'
import TaxonomyService from 'services/TaxonomyService'
import { SubjectWithTopics } from 'interfaces/Taxonomy'
import SubjectScrollList from 'components/modules/Subjects/SubjectScrollList'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import _useTranslation from 'lib/hooks/_useTranslation'
import SubjectTopics from 'components/feature/Taxonomy/SubjectTopics'
import { subjectsTopicRoute } from 'lib/constants/routes'
import CountryService from 'services/CountryService'
import { getLocalizedCountryOfOrigin } from 'lib/constants/country'
import { getTaxonomyCountryConfig } from 'lib/constants/taxonomy/taxonomyConfig'

const Container = styled.div`
    position: relative;
    min-height: 100vh;
    padding: 50px 15px;
    &::after {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        background: linear-gradient(180.02deg, #150620 0.01%, #14183a 50%, #150620 99.99%);
        z-index: -1;
        pointer-events: none;
    }
`

interface Props {
    subjects: SubjectInterface[]
    subjectTopics: SubjectWithTopics[]
}

function TaxonomySubtopic({ subjectTopics, subjects }: Props) {
    const { t } = _useTranslation('subjects')
    const md = useMedia(deviceSize.md)

    useEffect(() => {
        onPageEnter()
    }, [])

    return (
        <Container>
            <section>
                <Meta title={t('subjects/subjectsKnowsPageTitle')} description={t('subjects/subjectsSeoPageDescription')} />

                <SubjectScrollList
                    subjects={subjects}
                    referrerScreen="taxonomy"
                    subjectOnClickRedirect={(subject) => subjectsTopicRoute(subject)}
                    isDarkTheme
                />

                <Stack gutter={30}>
                    <Stack>
                        <StandardHeadline
                            textAlign={md ? 'center' : 'left'}
                            fontFamily="var(--font-inter)"
                            color={theme.colors.white}
                        >
                            {t('subjects/subjectsSeoLinkText')}
                        </StandardHeadline>
                        <StandardSubtitle
                            textAlign={md ? 'center' : 'left'}
                            fontFamily="var(--font-inter)"
                            fontWeight={400}
                            color={theme.colors.grayScaleSecondary}
                        >
                            {t('subjects/subjectsSeoPageDescription')}
                        </StandardSubtitle>
                    </Stack>
                    <Stack gutter={50}>
                        {subjectTopics?.length
                            ? subjectTopics.map((subjectTopic) => (
                                  <SubjectTopics key={subjectTopic.id} subjectTopics={subjectTopic} />
                              ))
                            : null}
                    </Stack>
                </Stack>
            </section>
        </Container>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
    const translations = await serverSideTranslations(parseLocale(context.locale).interfaceLanguageCode, [
        'common',
        'subjects',
    ])

    const parsedLocale = parseLocale(context.locale)

    const taxonomySubjectsIds = getTaxonomyCountryConfig(context.locale).map((subjects) => subjects.id)

    const countries = await CountryService.list()

    const countryId = countries.find((country) => getLocalizedCountryOfOrigin(country.code) === parsedLocale.countryCode)?.id

    if (!countryId) {
        return { notFound: true }
    }

    const subjectTopics = await TaxonomyService.getSubjectWithTaxonomyTopics(countryId).catch((error) => {
        throw error
    })

    if (!subjectTopics) {
        return { notFound: true }
    }

    const subjects = await SubjectService.list(parsedLocale.countryCode)

    return {
        props: {
            subjects: subjects.filter((subject) => taxonomySubjectsIds.includes(subject.id)) as Subject[],
            subjectTopics: subjectTopics,
            ...translations,
        },
    }
}

export default TaxonomySubtopic
